import {
	Application,
	CameraComponent,
	Entity,
	GraphNode,
	MeshInstance,
	Picker,
} from "playcanvas";
import { PickRequestContext, PickScheduler } from "./pick-scheduler";

/** Reads scene picks asynchronously and reports a changed canvas, camera or scene root as stale. */
export class ScenePicker {
	/** The pinned engine retains destroyed Pickers in device listeners, so this device's Picker is reused until disposal. */
	private _picker?: Picker;
	private _size?: { width: number; height: number };
	private _camera?: CameraComponent;
	private _root?: Entity;
	private _rect?: { x: number; y: number; width: number; height: number };
	constructor(
		private _canvas: HTMLCanvasElement,
		private _pickScheduler: PickScheduler,
		private _getApp: () => Application,
		private _getCamera: () => CameraComponent | undefined,
		private _onStale: () => void,
	) {}
	/** Records the current state, first reporting a change since the last record as stale. */
	public ensureState(): void {
		const width = this._canvas.offsetWidth;
		const height = this._canvas.offsetHeight;
		const camera = this._getCamera();
		const rect = this._canvas.getBoundingClientRect();
		const sceneRoot = camera ? this._getApp().root : undefined;
		if (
			this._size &&
			(this._size.width !== width ||
				this._size.height !== height ||
				this._camera !== camera ||
				this._root !== sceneRoot ||
				(this._rect &&
					(this._rect.x !== rect.x ||
						this._rect.y !== rect.y ||
						this._rect.width !== rect.width ||
						this._rect.height !== rect.height)))
		) {
			this._onStale();
		}
		this._size = { width, height };
		this._camera = camera;
		this._root = sceneRoot;
		this._rect = rect;
	}
	/** Forgets the recorded state, so the next record is not stale; the Picker is kept. */
	public invalidate(): void {
		this._size = undefined;
	}
	/** Returns undefined when all readback hits detached, rather than a true miss. */
	public async read(
		context: PickRequestContext,
		x: number,
		y: number,
		width = 1,
		height = 1,
	): Promise<MeshInstance[] | undefined> {
		const camera = this._getCamera();
		if (
			!context.isCurrent() ||
			!camera ||
			!this._canvas.offsetWidth ||
			!this._canvas.offsetHeight
		) {
			return [];
		}
		const app = this._getApp();
		const picker = (this._picker ??= new Picker(
			app,
			this._canvas.offsetWidth,
			this._canvas.offsetHeight,
		));
		// Resize and prepare only in the serialized lane, after prior readback.
		picker.resize(this._canvas.offsetWidth, this._canvas.offsetHeight);
		picker.prepare(camera, app.scene);
		const rect = this._canvas.getBoundingClientRect();
		const scaleX = this._canvas.offsetWidth / rect.width;
		const scaleY = this._canvas.offsetHeight / rect.height;
		const meshes = await picker.getSelectionAsync(
			(x - rect.x) * scaleX,
			(y - rect.y) * scaleY,
			width * scaleX,
			height * scaleY,
		);
		this.ensureState();
		if (
			!context.isCurrent() ||
			camera !== this._getCamera() ||
			this._size?.width !== this._canvas.offsetWidth ||
			this._size?.height !== this._canvas.offsetHeight
		) {
			return [];
		}
		const currentMeshes = meshes.filter((mesh) => {
			let node: GraphNode | null = mesh.node;
			while (node && node !== app.root) {
				node = node.parent;
			}
			return node === app.root;
		});
		return meshes.length && !currentMeshes.length ? undefined : currentMeshes;
	}
	/** Retires the Picker once the scheduler is idle. */
	public dispose(): void {
		const retired = this._picker;
		this._picker = undefined;
		if (retired) {
			void this._pickScheduler.whenIdle().then(() => retired.destroy());
		}
	}
}
